import { add, set } from "date-fns";
import { AppContext, BookingData, ServiceDuration, TimeSlot } from "../../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, Divider, Input, InputElement, Link, NavButtons, Notification } from "../../../common/components";
import { getCurrencyDisplay, getDurationDisplay, requireBookingDataFields, today } from "../../../utils";
import { marginBottom, marginRight, marginTop, marginY, padding, typography } from "../../../common/styles";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { applyOpacity } from "../../../common/styles/utils";
import debounce from "lodash/debounce";
import { IntervalListItem } from "./IntervalListItem/IntervalListItem";
import { Label } from "../../../common/components/Input/common";
import { NoAvailability } from "../NoAvailability/NoAvailability";
import { PartRequired } from "../../../utils/types";
import styled from "styled-components";
import { TimezoneSelector } from "../TimezoneSelector/TimezoneSelector";
import { useApolloClient } from "@apollo/client";
import { useGetTimeSlots } from "../hooks/useGetTimeSlots";
import { useTranslation } from "react-i18next";
type AvailabilityStepBookingData = PartRequired<BookingData, "organisation" | "service" | "timezone">;
type CompleteStepOpts = {
  attendees: number;
  duration: ServiceDuration;
  selectedTimeSlot: TimeSlot;
};
type ListViewProps = {
  createReservationLoading: boolean;
  onCompleteStep: (options: CompleteStepOpts) => void;
  onGoBack: () => void;
};
const CHUNK_SIZE = 50;
export function ListView(options: ListViewProps): React.ReactElement {
  const {
    createReservationLoading,
    onCompleteStep,
    onGoBack
  } = options;
  const {
    t,
    i18n
  } = useTranslation();
  const {
    appData
  } = useContext(AppContext);
  const client = useApolloClient();
  requireBookingDataFields<AvailabilityStepBookingData>(appData.bookingData, ["organisation", "timezone", "service"]);

  /**
   * The number of spaces required on a booking
   */
  const [attendees, setAttendees] = useState<number>(appData.bookingData.attendees ?? 1);
  const [monthOffset, setMonthOffset] = useState<number>(9);
  const [splitNumber, setSplitNumber] = useState<number>(CHUNK_SIZE);
  const [displayMonthOffset, setDisplayMonthOffset] = useState<boolean>(false);
  const {
    timezone,
    service,
    organisation,
    pricePoints
  } = appData.bookingData;
  const {
    durations
  } = service;
  const defaultDuration = appData.bookingData.duration ? appData.bookingData.duration : durations.find(duration => duration.default) ?? durations[0];

  /**
   * The duration of a booking
   */
  const [duration, setDuration] = useState<ServiceDuration>(defaultDuration);

  /**
   * Indicates whether the user searched further ahead for availability at least once.
   */
  const [hasSearchedFurtherAhead, setHasSearchedFurtherAhead] = useState<boolean>(false);

  /**
   * This is falling back to 500 because the value is currently incorrectly
   * marked as optional on the back-end but is a required field.
   *
   * It defaults to 500 in the rest of the Appointedd booking tools so
   * falling back to 500 for now until time is taken to make this a mandatory field.
   */
  const maxParticipantsPerBooking = service.maxParticipantsPerBooking ?? 500;

  // Generate nine months of date ranges for the availability request
  const ranges = [...new Array(9).keys()].map(count => {
    const baseDate = add(today, {
      months: count
    });
    const rangeStart = set(baseDate, {
      date: 1,
      hours: 0,
      minutes: 0,
      seconds: 0
    });
    const rangeEnd = set(add(baseDate, {
      months: 1
    }), {
      date: 1,
      hours: 0,
      minutes: 0,
      seconds: 0
    });
    return {
      end: rangeEnd.toISOString(),
      start: rangeStart.toISOString()
    };
  });
  const {
    timeSlots,
    loading: timeSlotsLoading,
    error: timeSlotsError,
    getMoreAvailability
  } = useGetTimeSlots({
    attendees,
    bookingData: appData.bookingData,
    bookingTool: appData.bookingTool,
    duration,
    ranges,
    requestRangesIndividually: true,
    selectedDate: today
  });
  const getMoreAvailableSlots = () => {
    if (splitNumber >= timeSlots.length) {
      setHasSearchedFurtherAhead(true);
      void getMoreAvailability({
        monthOffset,
        setMonthOffset
      }).then(value => {
        if (value.intervalCount > 0) {
          setSplitNumber(splitNumber + CHUNK_SIZE);
          setDisplayMonthOffset(false);
        } else {
          setDisplayMonthOffset(true);
        }
      });
    } else {
      setSplitNumber(splitNumber + CHUNK_SIZE);
    }
  };
  let displayDate = today;
  const currentSlice = timeSlots.slice(0, splitNumber);
  if (currentSlice.length > 0) {
    displayDate = new Date(currentSlice[currentSlice.length - 1].interval.end);
  }
  if (displayMonthOffset) {
    displayDate = add(today, {
      months: monthOffset
    });
  }
  const latestRangeEndDate = set(displayDate, {
    date: 1,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const latestRangeEndDateText = latestRangeEndDate.toLocaleDateString(i18n.language, {
    month: "long",
    year: "numeric"
  });
  const onAttendeesChange = (newAttendees: number | undefined) => {
    if (newAttendees) {
      void client.resetStore();
      setAttendees(newAttendees);
    }
  };
  const durationOptions = durations.map((duration, index) => {
    let valueString = getDurationDisplay(duration, t);
    const currency = organisation.currency;
    const price = duration.price ?? 0;
    const priceDisplay = price === 0 ? undefined : getCurrencyDisplay(price, currency, i18n.language);
    if (priceDisplay) {
      valueString += ` - ${priceDisplay}`;
    }
    const {
      oldPrice
    } = duration;
    if (typeof oldPrice === "number" && oldPrice > price) {
      const oldPriceDisplay = getCurrencyDisplay(oldPrice, currency, i18n.language);
      valueString += ` (${t("wasValue", {
        value: oldPriceDisplay
      })})`;
    }
    return {
      optionId: `${index}`,
      value: valueString
    };
  });
  const onDurationChange = (event: React.ChangeEvent<InputElement>) => {
    const selectedIndex = event.target.selectedIndex;
    const selectedDuration = durations.find((_d, index) => {
      return index === selectedIndex;
    });
    if (selectedDuration) {
      void client.clearStore().then(() => {
        setDuration(selectedDuration);
      });
    }
  };
  const showAttendeesInput = maxParticipantsPerBooking && maxParticipantsPerBooking > 1 && !pricePoints;
  const showDurationDropdown = durations.length > 1;
  const getSelectedOption = () => {
    const selectedOptionIndex = durations.findIndex(d => {
      return d.default === duration.default && d.minutes === duration.minutes && d.price === duration.price;
    });
    return `${selectedOptionIndex}`;
  };
  let prevMonthDisplay = "";
  const updateCalendarAvailability = useMemo(
  /**
   * debounce to give user time to put correct number of
   *  attendees before refreshinh availability
   * */
  () => debounce((numberOfAttendees: number | undefined) => onAttendeesChange(numberOfAttendees), 1000),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  useEffect(() => {
    return () => updateCalendarAvailability.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>
      {showAttendeesInput && <AttendeesContainer>
          <AttendeesLabel>{t("attendee_other")}</AttendeesLabel>
          <Input variant="number" value={attendees} disabled={timeSlotsLoading} id="attendeesNumberInput" aria-label={t("selectQuantity")} max={maxParticipantsPerBooking} min={1} onValueChange={quantity => updateCalendarAvailability(quantity)} />
        </AttendeesContainer>}

      {showDurationDropdown && <Input variant="dropdown" id={"durationDropdownInput"} aria-label={t("durationDropdownInput")} onChange={onDurationChange} label={t("duration_one")} options={durationOptions} disabled={timeSlotsLoading} hidePlaceholder selectedOption={getSelectedOption()} />}

      {(showAttendeesInput || showDurationDropdown) && <HorizontalDivider />}

      <TimezoneSelector disabled={timeSlotsLoading} data-sentry-element="TimezoneSelector" data-sentry-source-file="ListView.tsx" />

      {timeSlotsError && <ErrorNotification variant="warning">
          {t("errors.generic")}
        </ErrorNotification>}

      <TimeSlotList data-sentry-element="TimeSlotList" data-sentry-source-file="ListView.tsx">
        {timeSlotsLoading && <LoadingPill />}
        {timeSlots.length === 0 && !timeSlotsLoading && <NoAvailability hasSearchedFurtherAhead={hasSearchedFurtherAhead} monthOffset={monthOffset} onSearchMore={getMoreAvailableSlots} />}

        {currentSlice.map(slot => {
        const {
          interval
        } = slot;
        const monthDisplay = new Intl.DateTimeFormat(i18n.language, {
          month: "long",
          timeZone: timezone
        }).format(new Date(interval.start));
        const yearDisplay = new Intl.DateTimeFormat(i18n.language, {
          timeZone: timezone,
          year: "numeric"
        }).format(new Date(interval.start));
        const displayMonthYear = monthDisplay !== prevMonthDisplay;
        prevMonthDisplay = monthDisplay;
        return <React.Fragment key={slot.interval.start}>
              {displayMonthYear && <MonthYear key={`${monthDisplay}-${yearDisplay}`}>
                  <Month>{monthDisplay}</Month>
                  <Year>{yearDisplay}</Year>
                </MonthYear>}
              <IntervalListItem attendees={attendees} duration={duration} createReservationLoading={createReservationLoading} slot={slot} onCompleteStep={onCompleteStep} />
            </React.Fragment>;
      })}
      </TimeSlotList>

      {timeSlots.length > 0 && !timeSlotsLoading && !timeSlotsError && <SearchFurtherContainer>
          <DisplayUpToText>
            {t("displaySlotRangeEnd", {
          date: latestRangeEndDateText
        })}
          </DisplayUpToText>
          <SearchFurtherLink tabIndex={0} aria-label={t("searchFurtherAheadLinkAriaLabel")} onClick={getMoreAvailableSlots} onKeyPress={event => {
        if (event.key === "Enter") {
          getMoreAvailableSlots();
        }
      }}>
            {t("searchFurtherAheadLink")}
          </SearchFurtherLink>
        </SearchFurtherContainer>}

      <NavButtons {...{
      hideContinue: true,
      onBack: onGoBack
    }} data-sentry-element="NavButtons" data-sentry-source-file="ListView.tsx" />
    </>;
}
const AttendeesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${marginBottom.m}
`;
const AttendeesLabel = styled(Label)`
  text-transform: capitalize;
`;
const HorizontalDivider = styled(Divider).attrs({
  vertical: false
})`
  display: flex;
  ${marginBottom.m}
  ${marginTop.m}
`;
const TimeSlotList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  min-height: 10rem;
  ${padding.zero};
  ${marginBottom.l};
`;
const SearchFurtherContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;
const DisplayUpToText = styled.div`
  ${marginRight.s}
  ${typography.small}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
  &::first-letter {
    text-transform: capitalize;
  }
`;
const SearchFurtherLink = styled(Link)`
  ${typography.small}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const MonthYear = styled.div`
  display: flex;
`;
const Month = styled.div`
  ${typography.bodyBold}
  ${marginBottom.m}
  ${marginRight.s}
  /*
   * Was unset — renders {monthDisplay} text directly with no ambient
   * colour, so it silently fell back to the browser default black.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Year = styled.div`
  ${typography.body};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
`;
const LoadingPill = styled(BaseLoadingPill)`
  position: absolute;
  justify-self: center;
  align-self: center;
  z-index: 2;
`;
const ErrorNotification = styled(Notification)`
  ${marginY.m}
`;